/**
 * ConflictDialog — shown before regenerating Phase 0 artifacts when some artifacts have edits
 * in progress or are already committed. See specs/phase2/CR-D-artifact-editing-frontend-spec.md §5.
 *
 * The dialog itself performs no API calls — it presents the conflict and delegates the choice to
 * the parent via callbacks:
 *   • Regenerate (skip locked) → generate without `force` (backend skips pending/committed).
 *   • Force Overwrite Committed → generate with `force: true` (Leadership only).
 *
 * ── COPY CHANGED 2026-10-03 (P0 fix batch, Item 4 / §5.4f — F-1 resolved as option (a)) ────────
 * A committed artifact is no longer described as something that "requires Force Overwrite" to
 * change. Editing one now STARTS A NEW VERSION, through chat or the editor, and that is the route
 * the copy names. The previous wording implied the only way forward was a destructive leadership
 * override, which was never true — `patch-artifact.ts` has always permitted editing a committed
 * artifact — and is now actively misleading.
 *
 * The `force: true` path is NOT removed (§5.4e, deliberately). Full regeneration overwrites a
 * committed document with machine output WITHOUT a diff and WITHOUT review, which is categorically
 * different from a reviewable chat proposal or a deliberate manual edit. It stays leadership-only
 * and stays an escape hatch — it is just no longer presented as the normal path.
 */

import { CheckCircle2, Lock } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ARTIFACT_TYPES, ARTIFACT_LABELS } from "@/hooks/useArtifacts";

export interface ConflictDialogProps {
  open: boolean;
  /** Map of artifact type → current status. A missing entry is treated as overwritable. */
  artifactStatuses: Record<string, string>;
  userRole: string;
  onRegenerateSkipLocked: () => void;
  onForceOverwrite: () => void;
  onClose: () => void;
}

type Disposition = "overwrite" | "skip" | "force";

interface ArtifactRow {
  type: string;
  label: string;
  disposition: Disposition;
  note: string;
}

/** Classify how each artifact will be treated by a regeneration run. */
function disposition(status: string | undefined): Disposition {
  if (status === "pending_commit") return "skip";
  if (status === "committed") return "force";
  // generated / draft_edit / rejected / unknown → freely overwritten.
  return "overwrite";
}

const DISPOSITION_NOTE: Record<Disposition, string> = {
  overwrite: "Will be overwritten",
  skip: "PENDING APPROVAL — will be skipped",
  // Item 4 / §5.4f. This replaces "COMMITTED — requires Force Overwrite", which framed a
  // leadership override as the only way to change a committed file. It never was: editing one
  // starts a new version, and the committed version stays in the project's history.
  force:
    "COMMITTED — will be skipped; edit it through chat or the editor to start a new version",
};

export function ConflictDialog({
  open,
  artifactStatuses,
  userRole,
  onRegenerateSkipLocked,
  onForceOverwrite,
  onClose,
}: ConflictDialogProps): JSX.Element {
  const rows: ArtifactRow[] = ARTIFACT_TYPES.map((type) => {
    const d = disposition(artifactStatuses[type]);
    return {
      type,
      label: ARTIFACT_LABELS[type],
      disposition: d,
      note: DISPOSITION_NOTE[d],
    };
  });

  const hasCommitted = rows.some((r) => r.disposition === "force");
  const canForce = userRole === "leadership" && hasCommitted;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Some artifacts have edits in progress</DialogTitle>
          <DialogDescription>
            Regeneration will overwrite editable drafts. Artifacts pending
            approval are always skipped. Committed artifacts are skipped too —
            edit them through chat or the editor to start a new version, which
            keeps the committed version in the project&apos;s history.
          </DialogDescription>
        </DialogHeader>

        <ul
          className="space-y-2 py-1"
          aria-label="Artifact regeneration impact"
        >
          {rows.map((row) => {
            const locked = row.disposition !== "overwrite";
            return (
              <li
                key={row.type}
                className="flex items-start gap-3 rounded-md border border-outlines p-2.5 text-sm"
              >
                {locked ? (
                  <Lock
                    className={cn(
                      "mt-0.5 h-4 w-4 shrink-0",
                      row.disposition === "skip"
                        ? "text-amber-600"
                        : "text-red-600",
                    )}
                    aria-hidden="true"
                  />
                ) : (
                  <CheckCircle2
                    className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600"
                    aria-hidden="true"
                  />
                )}
                <span className="min-w-0">
                  <span className="font-medium text-text-primary">
                    {row.label}
                  </span>
                  <span
                    className={cn(
                      "ml-2 text-xs",
                      row.disposition === "skip" && "text-amber-700",
                      row.disposition === "force" && "text-red-700",
                      row.disposition === "overwrite" &&
                        "text-text-secondary",
                    )}
                  >
                    {row.note}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="outline" onClick={onRegenerateSkipLocked}>
            Regenerate (skip locked)
          </Button>
          {canForce && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="danger" onClick={onForceOverwrite}>
                    Force Overwrite Committed
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Last resort. Replaces committed artifacts with freshly
                  generated content, with no diff and no review — unlike a chat
                  proposal or a manual edit, which start a reviewable new
                  version. Artifacts pending approval are not affected.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConflictDialog;
